import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { dateOnlyFormat, dateTimeToS, dateUtcToS } from '../../util/dateUtil';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormLabel,
  Grid,
  Hidden,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
  TextField,
  Typography,
  TablePagination,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import TableStyle from '../../styles/TableStyle';
import { observer, inject } from 'mobx-react';
import { logException } from '../../services/appInsights';
import { btNavyBlueColor, btMediumGrayColor, btLightBlueColor, grayColor, secondaryColor } from '../../assets/jss/nextjs-material-kit-pro';
import PleaseWait from '../Shared/PleaseWait';
import { callApi } from '../../util/apiWrapper';
import ContactCards from './ContactCards';
import PageUnauthorized from '../Shared/PageUnauthorized';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import AddIcon from '@material-ui/icons/Add';
import ContactCheckinDetails from './ContactCheckinDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import HomeIcon from '@material-ui/icons/Home';
import { lookupSpecialistName, lookupSpecialistNameWithInitial, isStudent, isAdmin, getInitial } from '../../util/miscUtil';

const tableStyles = makeStyles(TableStyle);

const useStyles = makeStyles((theme) => ({
  frame: {
    width: '100vw',
  },
  alignPage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  formatTableSize: {
    margin: '0 auto 0 auto',
    maxWidth: '1320px',
  },

  noLeftPadding: {
    paddingLeft: '0 !important',
  },
  sortHeader: {
    cursor: 'pointer',
    color: btNavyBlueColor,
  },
  nosortHeader: {
    color: btMediumGrayColor,
  },
  approvalLink: {
    fontSize: '0.85rem',
    padding: '2px, 5px',
    textTransform: 'none',
    justifyContent: 'center',
  },
  idNoLink: {
    padding: 0,
  },
  gridLink: {
    color: btNavyBlueColor,
    fontSize: '0.75rem',
    padding: 0,
    '&:hover': {
      cursor: 'pointer',
    },
    textTransform: 'none',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  centerAlignContent: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px',
  },
  filter: {
    margin: '5px 0 15px 20px',
  },
  switchLeadingText: {
    margin: '5px 20px 15px 0',
  },
  noRecordsMsg: {
    fontSize: '1.15rem',
    margin: 5,
  },
  whiteBg: {
    background: '#FFFFFF',
  },
  centerRadioSorting: {
    marginLeft: 20,
    marginRight: 20,
    justifyContent: 'space-between',
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
  },
  selectDropdown: {
    color: '#333',
    backgroundColor: '#fff',
  },
  menuItem: {
    '&:hover': {
      color: '#fff',
      backgroundColor: '#8c857b',
    },
  },
  detailsLink: {
    cursor: 'pointer',
    margin: '5px 0 0 10px',
  },
  rowSpacer: {
    margin: '10px 0',
  },
  question: {
    padding: '7px 5px 7px 0',
  },
  answer: {
    padding: '7px 0',
  },
  breadcrumbs: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      minWidth: '95%',
      padding: '18px 0 20px 5px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '95vw',
      padding: '18px 0 0 10px',
    },
  },
  breadCrumbLink: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    fontSize: '0.80rem',
    cursor: 'pointer',
    '&:hover,&:focus': {
      textDecoration: 'underline',
    },
    color: grayColor,
  },
  breadCrumbText: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    color: secondaryColor,
    fontSize: '0.80rem',
  },
}));

const ContactList = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;
    const history = useHistory();
    const classes = useStyles();
    const tableClasses = tableStyles();

    const [pageState, setPageState] = useState({
      allRecords: [],
      records: [],
      filter: '',
      fetchedRecords: false,
      loading: false,
      showAllRecords: false,
      showMyRecords: false,
      sortDirection: 'asc',
      sortBy: '',
    });
    const [errorMsg, setErrorMsg] = useState(null);
    const [pageOptions, setPageOptions] = useState({
      searching: false,
      page: 0,
      perPage: 15,
    });
    const [filter, setFilter] = React.useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [contactHistory, setContactHistory] = useState([]);
    const [historyRecord, setHistoryRecord] = useState(null);
    const [showHistory, setShowHistory] = useState(null);
    // const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyRecs, setHistoryRecs] = useState([]);

    const getStaffId = (userId) => {
      const idx = store.staffList.findIndex((s) => s.user.id === userId);
      if (idx > -1) {
        return store.staffList[idx].id;
      }
    };

    useEffect(() => {
      const fetchStaffList = async () => {
        try {
          store.setLoading(true);
          store.setSearching(true);
          const staff = await callApi(auth, 'staff', 'GET');
          store.setStaffList(staff.result);
          store.setSearching(false);
          store.setLoading(false);
        } catch (error) {
          store.setSearching(false);
          store.setLoading(false);
          console.warn('Error fetching aftercare specialist list:', error);
          logException(error);
        }
      };

      const fetchRecords = async () => {
        try {
          setPageState({ ...pageState, loading: true });
          const rsp = await callApi(auth, 'contactlist', 'GET');
          let list = rsp.result;
          list.forEach((r, i) => {
            r.due_date_formatted = dateUtcToS(r.due_date);
            r.due_date_datetime_formatted = dateTimeToS(r.due_date);
            r.last_attempt_date_formatted = dateTimeToS(r.last_attempt_date);
          });

          setPageState({
            ...pageState,
            loading: false,
            records: [...list],
            allRecords: [...list],
            sortBy: 'due_date',
            fetchedRecords: true,
            showAllRecords: false,
          });
        } catch (error) {
          logException(error);
          setPageState({ ...pageState, loading: false, fetchedRecords: true });
          setErrorMsg('An unexpected error has occurred. Please try again.');
          console.log('Error fetching list of contact records:', error);
        }
      };
      if (auth.authToken && !pageState.fetchedRecords && !pageState.loading) {
        fetchStaffList();
        fetchRecords();
      }
    }, [auth, auth.authToken]);

    useEffect(() => {
      if (auth.roles && auth.roles.length > 0) {
        if (isStudent(auth.roles)) {
          history.push('/seniorplanner');
        }
      }
    }, [auth.roles, history]);

    useEffect(() => {
      const compareValues = (key, order = 'asc') => {
        return function innerSort(a, b) {
          const varA = !a.hasOwnProperty(key)
            ? ''
            : key === 'id'
            ? a[key].toString()
            : key === 'type' || key === 'status' || key === 'last_attempt_mode' || key === 'last_attempt_result'
            ? a[key].toLowerCase()
            : key === 'specialist_id' || key === 'submitted_by'
            ? lookupSpecialistName(a[key], store.staffList)
            : key === 'due_date_formatted' || key === 'last_attempt_date_formatted'
            ? dateOnlyFormat(a[key]).toString()
            : a[key];
          const varB = !b.hasOwnProperty(key)
            ? ''
            : key === 'id'
            ? a[key].toString()
            : key === 'type' || key === 'status' || key === 'last_attempt_mode' || key === 'last_attempt_result'
            ? b[key].toLowerCase()
            : key === 'specialist_id' || key === 'submitted_by'
            ? lookupSpecialistName(b[key], store.staffList)
            : key === 'due_date_formatted' || key === 'last_attempt_date_formatted'
            ? dateOnlyFormat(b[key]).toString()
            : b[key];
          let comparison = 0;
          if (varA > varB) {
            comparison = 1;
          } else if (varA < varB) {
            comparison = -1;
          }
          return order === 'desc' ? comparison * -1 : comparison;
        };
      };

      let filteredData = pageState.allRecords;

      if (filter && filter.length > 0) {
        const filterNoSpaces = (filter || '').replace(/\s+/g, '');
        const filterLower = filterNoSpaces.toLowerCase();

        filteredData = filteredData.filter(
          (s) =>
            (s.first_name && s.first_name.toLowerCase().includes(filterLower)) ||
            (s.last_name && s.last_name.toLowerCase().includes(filterLower)) ||
            (s.alumni_updated_first_name && s.alumni_updated_first_name.toLowerCase().includes(filterLower)) ||
            (s.alumni_updated_last_name && s.alumni_updated_last_name.toLowerCase().includes(filterLower)) ||
            (s.int_id && s.int_id.replace(/\s+/g, '').includes(filterNoSpaces)) ||
            (s.specialist_id && lookupSpecialistName(s.specialist_id, store.staffList).replace(/\s+/g, '').toLowerCase().includes(filterLower)) ||
            (s.submitted_by && lookupSpecialistName(s.submitted_by, store.staffList).replace(/\s+/g, '').toLowerCase().includes(filterLower)) ||
            (s.type && s.type.toLowerCase().includes(filterLower)) ||
            (s.due_date_formatted && s.due_date_formatted.replace(/\s+/g, '').includes(filterNoSpaces)) ||
            (s.last_attempt_date_formatted && s.last_attempt_date_formatted.replace(/\s+/g, '').includes(filterNoSpaces)) ||
            (s.status && s.status.toLowerCase().includes(filterLower))
        );
      }

      let sortedRecords = filteredData;
      if (sortedRecords) {
        if (!pageState.showAllRecords) {
          sortedRecords = sortedRecords.filter((c) => c.status !== 'closed');
        }
        if (pageState.showMyRecords) {
          const staffId = getStaffId(auth.user.result.id);
          sortedRecords = sortedRecords.filter((r) => r.specialist_id === staffId);
        }
        sortedRecords.sort(compareValues(pageState.sortBy, pageState.sortDirection));
        setPageState((p) => {
          return { ...p, records: sortedRecords };
        });
      }
    }, [pageState.allRecords, pageState.sortBy, pageState.sortDirection, filter, pageState.showAllRecords, pageState.showMyRecords]);

    const setSort = (val) => {
      let direction = 'asc';
      if (val === pageState.sortBy) {
        if (pageState.sortDirection === 'asc') {
          direction = 'desc';
        } else {
          direction = 'asc';
        }
        setPageState({ ...pageState, sortDirection: direction });
      } else {
        setPageState({ ...pageState, sortDirection: 'asc', sortBy: val });
      }
    };

    const filterData = (val) => {
      setFilter(val);
    };

    // const handleOpenAlumni = (event, id) => {
    //   event.preventDefault();
    //   event.stopPropagation();
    //   store.setBasicInfoId(id);
    //   store.setAlumni(true);
    //   history.push('/seniorplanner');
    // };

    const convertResult = (result) => {
      switch (result) {
        case 'WrongNumber':
          return 'Wrong Number';
        case 'NoAnswer':
          return 'No Answer';
        case 'VoiceMail':
          return 'Voice Mail';
        case 'OtherContacts':
          return 'Other Contacts';
        case 'EmailSent':
          return 'Email Sent';
        case 'TextSent':
          return 'Text Sent';
        case 'SocialMediaContact':
          return 'Social Media';
        default:
          return result;
      }
    };

    const handleHideHistory = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setShowHistory(null);
    };

    const handleShowHistory = (event, rec) => {
      event.preventDefault();
      event.stopPropagation();
      fetchContactHistory(rec.alumni_id);
      setShowHistory(rec.id);
    };

    const handleOpenCheckin = async (event, id) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        store.setLoading(true);
        store.setSearching(true);
        let ct = await callApi(auth, 'contact/' + id, 'GET');
        store.setCheckIn(ct.result);
        const ctUserId = ct.result.user_id;
        store.setCheckInId(ctUserId);
        let senior = await callApi(auth, 'studentplan/' + ctUserId, 'GET');
        store.setSenior(senior.result);
        store.setSearching(false);
        store.setLoading(false);
        history.push('/contact');
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.log('Error fetching opening checkin:', error);
      }
    };

    const fetchSeniorInformation = async () => {
      try {
        store.setLoading(true);
        store.setSearching(true);
        let senior = await callApi(auth, 'studentplan/' + store.checkInId, 'GET');
        store.setSenior(senior.result);
        store.setSearching(false);
        store.setLoading(false);
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.log('Error fetching senior information:', error);
      }
    };

    const handleDialogClose = () => {
      setDialogOpen(false);
    };

    // const handleCardSortChange = (event) => {
    //   setPageState({ ...pageState, sortBy: event.target.value });
    // };

    const displayAttemptMode = (mode) => {
      switch (mode) {
        case 'FocusMeeting':
          return 'Focus Meeting';
        case 'SocialMedia':
          return 'Social Media';
        default:
          return mode;
      }
    };

    // Gets List Record and opens details dialog. (From table)
    const fetchContactDetails = async (alumniId) => {
      try {
        const res = await callApi(auth, 'contact/history/' + alumniId, 'GET');
        const list = res.result;
        setContactHistory(list);
        setHistoryRecord(list[0]);
        setDialogOpen(true);
      } catch (error) {
        console.log('Error fetching contact details:', error);
        logException(error);
      }
    };

    // Gets History Record and opens details dialog. (From expanded History in table)
    const fetchHistoryDetails = (id, attempt) => {
      if (!id || id === 0) return null;
      const idx = historyRecs.findIndex((s) => s.id === id && s.contact_attempt === attempt);
      if (idx > -1) {
        setHistoryRecord(historyRecs[idx]);
        setDialogOpen(true);
      }
    };

    const createSpecialistDict = () => {
      const dict = Object.assign({}, ...store.staffList.map((x) => ({ [x.id]: getInitial(x.user.first_name) + '. ' + x.user.last_name })));
      return dict;
    };

    const fetchContactHistory = async (id) => {
      try {
        const res = await callApi(auth, 'contact/history/' + id, 'GET');
        setHistoryRecs(res.result);
      } catch (error) {
        console.log('Error fetching contact history:', error);
        logException(error);
      }
    };

    // const handleHistoryDialogClose = () => {
    //   setHistoryDialogOpen(false);
    // };

    const emptyRows =
      pageOptions.perPage - Math.min(pageOptions.perPage, pageState.records && pageState.records.length - pageOptions.page * pageOptions.perPage);

    if (auth.roles && auth.roles.length > 0 && !isAdmin(auth.roles)) {
      return <PageUnauthorized />;
    }

    return (
      <div className={classes.frame}>
        {auth.roles && auth.roles.length > 0 && isAdmin(auth.roles) && (
          <Grid container className={classes.whiteBg}>
            <React.Fragment>
              <PleaseWait isLoading={pageState.loading} />
              <div className={classes.breadcrumbs} style={{ margin: '10px 5px' }}>
                <Breadcrumbs separator='›' aria-label='breadcrumbs' className={classes.alignCrumb}>
                  <span className={classes.breadCrumbLink} style={{ marginTop: '-2px' }} onClick={(e) => history.push('/')}>
                    <HomeIcon style={{ fontSize: '1.30rem' }} />
                    <span style={{ margin: '1px 0 0 1px' }}>Home</span>
                  </span>
                  <span className={classes.breadCrumbText} style={{ marginTop: '-1px' }}>
                    Proactive Check-Ins
                  </span>
                </Breadcrumbs>
              </div>

              <div className={classes.frame}>
                <div className={classes.alignPage}>
                  <Grid container className={classes.formatTableSize}>
                    <Grid item xs={12}>
                      <FormLabel error>{errorMsg}</FormLabel>
                    </Grid>

                    <Grid container className={classes.whiteBg} align='left'>
                      <Grid item xs={6} className={classes.whiteBg} align='left'></Grid>
                      <Grid item xs={6} align='right' className={classes.whiteBg}></Grid>

                      <Grid item xs={12}>
                        {store.checkInId && store.checkInId > 0 ? (
                          <div></div>
                        ) : (
                          <>
                            <Grid container direction='row' style={{ marginBottom: '20px' }}>
                              <Grid item xs={12} sm={8} md={4} lg={4} className={classes.whiteBg} align='left'>
                                <TextField
                                  id='filter-field'
                                  type='search'
                                  className={classes.filter}
                                  size='small'
                                  hiddenLabel
                                  placeholder='Filter by client, specialist, or type'
                                  variant='outlined'
                                  onChange={(event) => filterData(event.target.value)}
                                  clearable='true'
                                  style={{ width: '75%' }}
                                />
                              </Grid>
                              <Grid item xs={1} sm={5} md={4} lg={5}></Grid>
                              <Grid item xs={11} sm={6} md={4} lg={3} align='left' className={classes.whiteBg}>
                                <div>
                                  <FormControlLabel
                                    control={
                                      <Tooltip title='Show only my clients.' placement='top'>
                                        <Checkbox
                                          color='default'
                                          checked={pageState.showMyRecords}
                                          onChange={(e) =>
                                            setPageState((p) => {
                                              return { ...p, showMyRecords: !pageState.showMyRecords };
                                            })
                                          }
                                        ></Checkbox>
                                      </Tooltip>
                                    }
                                    label='My Check-Ins'
                                  />
                                </div>
                                <div>
                                  <FormControlLabel
                                    control={
                                      <Tooltip title='Show closed check-in records.' placement='top'>
                                        <Checkbox
                                          color='default'
                                          checked={pageState.showAllRecords}
                                          onChange={(e) =>
                                            setPageState((p) => {
                                              return { ...p, showAllRecords: !pageState.showAllRecords };
                                            })
                                          }
                                        ></Checkbox>
                                      </Tooltip>
                                    }
                                    label='Show Closed Check-Ins'
                                  />
                                </div>
                              </Grid>
                            </Grid>
                          </>
                        )}

                        {/* Grid/Table */}
                        <Hidden mdDown implementation='css'>
                          <TableContainer component={Paper}>
                            <Table className={tableClasses.table}>
                              <TableHead>
                                <TableRow>
                                  <TableCell className={classes.noSortHeader} style={{ width: '100px' }}></TableCell>
                                  <Tooltip title='Click to sort by client.' placement='top'>
                                    <TableCell onClick={() => setSort('last_name')} className={classes.sortHeader} style={{ width: '200px' }}>
                                      CLIENT{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_name' ? (pageState.sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_name'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by specialist.' placement='top'>
                                    <TableCell onClick={() => setSort('specialist_id')} className={classes.sortHeader} style={{ width: '150px' }}>
                                      SPECIALIST{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'specialist_id'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'specialist_id'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by submitted by.' placement='top'>
                                    <TableCell onClick={() => setSort('submitted_by')} className={classes.sortHeader} style={{ width: '130px' }}>
                                      SUBMITTED BY{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'submitted_by'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'submitted_by'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by type.' placement='top'>
                                    <TableCell onClick={() => setSort('type')} className={classes.sortHeader} style={{ width: '80px' }}>
                                      TYPE{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'type' ? (pageState.sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'type' ? (pageState.sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by due date.' placement='top'>
                                    <TableCell
                                      onClick={() => setSort('due_date_formatted')}
                                      className={classes.sortHeader}
                                      style={{ width: '160px' }}
                                    >
                                      DUE ON{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'due_date_formatted'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'due_date_formatted'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by last attempt date.' placement='top'>
                                    <TableCell
                                      onClick={() => setSort('last_attempt_date_formatted')}
                                      className={classes.sortHeader}
                                      style={{ width: '160px' }}
                                    >
                                      LAST ATTEMPT{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_date_formatted'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_date_formatted'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by mode.' placement='top'>
                                    <TableCell onClick={() => setSort('last_attempt_mode')} className={classes.sortHeader} style={{ width: '110px' }}>
                                      CONTACT MODE{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_mode'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_mode'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by result.' placement='top'>
                                    <TableCell
                                      onClick={() => setSort('last_attempt_result')}
                                      className={classes.sortHeader}
                                      style={{ width: '150px' }}
                                    >
                                      LAST RESULT{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_result'
                                              ? pageState.sortDirection === 'asc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display:
                                            pageState.sortBy === 'last_attempt_result'
                                              ? pageState.sortDirection === 'desc'
                                                ? 'inline-block'
                                                : 'none'
                                              : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <TableCell className={classes.noSortHeader} style={{ width: '50px' }}></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {pageOptions.searching ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8}>
                                      <Typography variant='h5'>Searching...</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  pageState.records &&
                                  pageState.records
                                    .slice(pageOptions.page * pageOptions.perPage, pageOptions.page * pageOptions.perPage + pageOptions.perPage)
                                    .map((record, idx) => (
                                      <React.Fragment key={`contact-${record.id}`}>
                                        <TableRow>
                                          {record.status === 'closed' ? (
                                            <>
                                              <TableCell style={{ width: '100px', paddingLeft: 10, height: '40px' }}></TableCell>
                                              <TableCell style={{ width: '200px', color: btMediumGrayColor }}>
                                                {record && record.alumni_updated_last_name && record.alumni_updated_last_name.length > 0
                                                  ? record.alumni_updated_last_name
                                                  : record.last_name}
                                                {', '}
                                                {record && record.alumni_updated_first_name && record.alumni_updated_first_name.length > 0
                                                  ? record.alumni_updated_first_name
                                                  : record.first_name}{' '}
                                                ({record.int_id})
                                              </TableCell>
                                            </>
                                          ) : (
                                            <>
                                              <TableCell style={{ width: '100px', paddingLeft: 10 }}>
                                                <IconButton
                                                  size='small'
                                                  aria-label='Add Check-In'
                                                  onClick={(ev) => handleOpenCheckin(ev, record.id)}
                                                  color='primary'
                                                  style={{ padding: '5px', margin: 0, fontSize: '.80rem' }}
                                                >
                                                  <AddIcon fontSize='small' /> Add New
                                                </IconButton>
                                              </TableCell>
                                              <TableCell style={{ width: '200px' }}>
                                                {record && record.alumni_updated_last_name && record.alumni_updated_last_name.length > 0
                                                  ? record.alumni_updated_last_name
                                                  : record.last_name}
                                                {', '}
                                                {record && record.alumni_updated_first_name && record.alumni_updated_first_name.length > 0
                                                  ? record.alumni_updated_first_name
                                                  : record.first_name}{' '}
                                                ({record.int_id})
                                              </TableCell>
                                            </>
                                          )}
                                          <TableCell
                                            style={{
                                              width: '150px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                            }}
                                          >
                                            {lookupSpecialistNameWithInitial(record.specialist_id, store.staffList)}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '130px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                            }}
                                          >
                                            {lookupSpecialistNameWithInitial(record.submitted_by, store.staffList)}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '80px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                            }}
                                          >
                                            {record.type}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '160px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                              fontWeight: record.last_attempt_result === 'Reschedule' ? 600 : 400,
                                            }}
                                          >
                                            {record.last_attempt_result === 'Reschedule'
                                              ? record.due_date_datetime_formatted
                                              : record.due_date_formatted}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '160px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                            }}
                                          >
                                            {record.last_attempt_date_formatted}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '110px',
                                              color: record.status === 'closed' ? btMediumGrayColor : 'inherit',
                                            }}
                                          >
                                            {displayAttemptMode(record.last_attempt_mode)}
                                          </TableCell>
                                          <TableCell
                                            style={{
                                              width: '150px',
                                              marginTop: '12px',
                                              display: 'flex',
                                              flexDirection: 'row',
                                              justifyContent: 'flex-start',
                                              alignItems: 'center',
                                            }}
                                          >
                                            {convertResult(record.last_attempt_result)}{' '}
                                            {record.last_attempt_result && (
                                              <span className={classes.detailsLink} onClick={(e) => fetchContactDetails(record.alumni_id)}>
                                                <RemoveRedEyeIcon style={{ color: btLightBlueColor }} />
                                              </span>
                                            )}
                                          </TableCell>
                                          {record.last_attempt_result && record.last_attempt_result.length > 0 && (
                                            <TableCell style={{ width: '50px', paddingLeft: 10 }}>
                                              {showHistory !== record.id && (
                                                <IconButton
                                                  size='small'
                                                  aria-label='Show history'
                                                  onClick={(ev) => handleShowHistory(ev, record)}
                                                  color='primary'
                                                  style={{ marginTop: '-7px', fontSize: '.80rem' }}
                                                >
                                                  <ExpandMoreIcon fontSize='small' />
                                                </IconButton>
                                              )}
                                              {showHistory === record.id && (
                                                <IconButton
                                                  size='small'
                                                  aria-label='Hide history'
                                                  onClick={(ev) => handleHideHistory(ev)}
                                                  color='primary'
                                                  style={{ marginTop: '-7px', fontSize: '.80rem' }}
                                                >
                                                  <ExpandLessIcon fontSize='small' />
                                                </IconButton>
                                              )}
                                            </TableCell>
                                          )}
                                        </TableRow>
                                        {/* Child Table */}
                                        <TableRow>
                                          <TableCell style={{ padding: 0 }} colSpan={10}>
                                            <Collapse in={showHistory === record.id} timeout='auto' unmountOnExit>
                                              <Box>
                                                <Table aria-label='Check-In History'>
                                                  <TableBody>
                                                    <TableRow key={record.i + 'history'} style={{ padding: '-10px 0', height: '22px' }}>
                                                      <TableCell style={{ padding: 0 }}>
                                                        <Table className={tableClasses.table} style={{ marginBottom: '-1px' }}>
                                                          <TableBody>
                                                            {historyRecs.map((historyRecord, idx) => (
                                                              <TableRow
                                                                key={`history-${historyRecord.id}-${historyRecord.contact_attempt}-${idx}`}
                                                                style={{ borderBottom: 0, backgroundColor: '#f5f5f5', height: '22px' }}
                                                              >
                                                                <TableCell style={{ width: '100px' }}> </TableCell>
                                                                <TableCell style={{ width: '200px' }}> </TableCell>
                                                                <TableCell style={{ width: '150px' }}> </TableCell>
                                                                <TableCell style={{ width: '130px', color: '#696969' }}>
                                                                  <div
                                                                    style={{
                                                                      marginLeft: '10px',
                                                                      display: 'flex',
                                                                      flexDirection: 'row',
                                                                      justifyContent: 'flex-start',
                                                                      alignItems: 'flex-start',
                                                                    }}
                                                                  >
                                                                    {createSpecialistDict()[historyRecord.specialist_id]}
                                                                  </div>
                                                                </TableCell>
                                                                <TableCell
                                                                  style={{
                                                                    width: '80px',
                                                                    color: '#696969',
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      marginLeft: '15px',
                                                                      display: 'flex',
                                                                      flexDirection: 'row',
                                                                      justifyContent: 'flex-start',
                                                                      alignItems: 'flex-start',
                                                                    }}
                                                                  >
                                                                    {historyRecord.type}
                                                                  </div>
                                                                </TableCell>
                                                                <TableCell
                                                                  style={{
                                                                    marginLeft: '25px',
                                                                    width: '179px',
                                                                  }}
                                                                >
                                                                  <div style={{ marginLeft: '-11px' }}>
                                                                    {/* {historyRecord.result === 'Reschedule'
                                                                      ? dateTimeUtcToS(historyRecord.due_date)
                                                                      : dateUtcToS(historyRecord.due_date)} */}
                                                                  </div>
                                                                </TableCell>
                                                                <TableCell
                                                                  style={{
                                                                    width: '178px',
                                                                    color: '#696969',
                                                                  }}
                                                                >
                                                                  <div>{dateTimeToS(historyRecord.contact_date)}</div>
                                                                </TableCell>
                                                                <TableCell
                                                                  style={{
                                                                    width: '114px',
                                                                  }}
                                                                >
                                                                  <br />
                                                                </TableCell>
                                                                <TableCell
                                                                  colSpan={2}
                                                                  style={{
                                                                    width: '160px',
                                                                    color: '#696969',
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      marginLeft: '-15px',
                                                                      display: 'flex',
                                                                      flexDirection: 'row',
                                                                      justifyContent: 'flex-start',
                                                                      alignItems: 'center',
                                                                    }}
                                                                  >
                                                                    {displayAttemptMode(historyRecord?.contact_mode)}{' '}
                                                                    {historyRecord?.contact_mode?.length > 0 && (
                                                                      <span
                                                                        className={classes.subDetailsLink}
                                                                        onClick={(e) =>
                                                                          fetchHistoryDetails(historyRecord.id, historyRecord.contact_attempt)
                                                                        }
                                                                      >
                                                                        <RemoveRedEyeIcon
                                                                          fontSize='small'
                                                                          style={{ margin: '5px 0 0 5px', color: btLightBlueColor }}
                                                                        />
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                </TableCell>
                                                              </TableRow>
                                                            ))}
                                                            {!history || history.length === 0 ? (
                                                              <TableRow>
                                                                <TableCell align='center' colSpan={11} className={classes.rowSpacer}>
                                                                  <summary className={classes.noRecordsMsg}>No records found.</summary>
                                                                </TableCell>
                                                              </TableRow>
                                                            ) : (
                                                              <React.Fragment />
                                                            )}
                                                          </TableBody>
                                                        </Table>
                                                      </TableCell>
                                                    </TableRow>
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </React.Fragment>
                                    ))
                                )}
                                {!pageOptions.searching && pageState.records && pageState.records.length === 0 ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8} className={classes.noLeftPadding}>
                                      <summary className={classes.noRecordsMsg}>No records found.</summary>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <React.Fragment />
                                )}
                                {emptyRows > 0 && (
                                  <TableRow style={{ height: 34 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <TablePagination
                              component='div'
                              colSpan={8}
                              SelectProps={{
                                InputProps: { 'aria-label': 'rows per page' },
                                MenuProps: { classes: { paper: classes.selectDropdown } },
                              }}
                              classes={{ menuItem: classes.menuItem }}
                              rowsPerPageOptions={[10, 15, 25, 50, 100]}
                              count={pageState.records ? pageState.records.length : 0}
                              rowsPerPage={pageOptions.perPage}
                              page={pageOptions.page}
                              onPageChange={(ev, val) => {
                                setPageOptions({ ...pageOptions, page: val });
                              }}
                              onRowsPerPageChange={(ev) => setPageOptions({ ...pageOptions, perPage: parseInt(ev.target.value, 10), page: 0 })}
                              showFirstButton
                              showLastButton
                            />
                          </TableContainer>
                        </Hidden>

                        {/* Cards */}
                        <Hidden lgUp implementation='css'>
                          <ContactCards list={pageState.records} open={dialogOpen} handleOpenCheckin={handleOpenCheckin} />
                        </Hidden>
                      </Grid>
                    </Grid>
                  </Grid>
                </div>
              </div>

              <ContactCheckinDetails
                handleDialogClose={handleDialogClose}
                historyRecord={historyRecord}
                classes={classes}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
              />
            </React.Fragment>
          </Grid>
        )}
      </div>
    );
  })
);

export default ContactList;
